import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { announcementApi, type CreateAnnouncementPayload } from '../api';
import type {
  ChangeAnnouncementStatusPayload,
  ChangeAnnouncementStatusResponse,
  CreateAnnouncementRequest,
  DeleteAnnouncementResponse,
  UpdateAnnouncementRequest,
} from '../types';
import {
  showApiError,
  createRetryHandler,
} from '../../../shared/utils/apiErrorHandler';

export const useAnnouncementMutations = () => {
  const queryClient = useQueryClient();

  // Create announcement mutation with retry logic
  const createAnnouncement = useMutation({
    mutationFn: (data: CreateAnnouncementRequest) => {
      const retryHandler = createRetryHandler(3, 1000);
      return retryHandler(() => announcementApi.createAnnouncement(data));
    },
    onSuccess: (_response) => {
      toast.success('Announcement created successfully!');
      // Invalidate and refetch announcements
      queryClient.invalidateQueries({ queryKey: ['announcements'] });
    },
    onError: (error: any) => {
      showApiError(error, 'Failed to create announcement');
    },
  });

  // Update announcement mutation with retry logic
  const updateAnnouncement = useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: UpdateAnnouncementRequest;
    }) => {
      const retryHandler = createRetryHandler(3, 1000);
      return retryHandler(() => announcementApi.updateAnnouncement(id, data));
    },
    onSuccess: (updatedAnnouncement) => {
      toast.success('Announcement updated successfully!');
      // Update the specific announcement in cache
      queryClient.setQueryData(
        ['announcements', updatedAnnouncement.id],
        updatedAnnouncement,
      );
      // Invalidate announcements list
      queryClient.invalidateQueries({ queryKey: ['announcements'] });
    },
    onError: (error: any) => {
      showApiError(error, 'Failed to update announcement');
    },
  });

  // Delete announcement mutation
  const deleteAnnouncement = useMutation({
    mutationFn: (id: string) => announcementApi.deleteAnnouncement(id),
    onSuccess: (_, deletedId) => {
      toast.success('Announcement deleted successfully!');
      // Remove from cache
      queryClient.removeQueries({ queryKey: ['announcements', deletedId] });
      // Invalidate announcements list
      queryClient.invalidateQueries({ queryKey: ['announcements'] });
    },
    onError: (error: any) => {
      showApiError(error, 'Failed to delete announcement');
    },
  });

  // Mark as read mutation
  const markAsRead = useMutation({
    mutationFn: (id: string) => announcementApi.markAsRead(id),
    onSuccess: (_, announcementId) => {
      // Invalidate announcements to refresh read status
      queryClient.invalidateQueries({ queryKey: ['announcements'] });
      queryClient.invalidateQueries({
        queryKey: ['announcements', announcementId],
      });
    },
    onError: (error: any) => {
      showApiError(error, 'Failed to mark announcement as read');
    },
  });

  return {
    createAnnouncement: {
      mutate: createAnnouncement.mutate,
      mutateAsync: createAnnouncement.mutateAsync,
      isLoading: createAnnouncement.isPending,
      error: createAnnouncement.error,
      isError: createAnnouncement.isError,
      isSuccess: createAnnouncement.isSuccess,
      reset: createAnnouncement.reset,
    },
    updateAnnouncement: {
      mutate: updateAnnouncement.mutate,
      mutateAsync: updateAnnouncement.mutateAsync,
      isLoading: updateAnnouncement.isPending,
      error: updateAnnouncement.error,
      isError: updateAnnouncement.isError,
      isSuccess: updateAnnouncement.isSuccess,
      reset: updateAnnouncement.reset,
    },
    deleteAnnouncement: {
      mutate: deleteAnnouncement.mutate,
      mutateAsync: deleteAnnouncement.mutateAsync,
      isLoading: deleteAnnouncement.isPending,
      error: deleteAnnouncement.error,
      isError: deleteAnnouncement.isError,
      isSuccess: deleteAnnouncement.isSuccess,
      reset: deleteAnnouncement.reset,
    },
    markAsRead: {
      mutate: markAsRead.mutate,
      mutateAsync: markAsRead.mutateAsync,
      isLoading: markAsRead.isPending,
      error: markAsRead.error,
      isError: markAsRead.isError,
      isSuccess: markAsRead.isSuccess,
      reset: markAsRead.reset,
    },
  };
};

export const useDeleteResidentAnnouncement = () => {
  const queryClient = useQueryClient();

  return useMutation<DeleteAnnouncementResponse, Error, string>({
    mutationFn: (id) => announcementApi.deleteAnnouncement(id),
    onSuccess: (data) => {
      toast.success(data.message || 'Announcement deleted successfully');
      // ✅ Invalidate the list so it's refetched
      queryClient.invalidateQueries({ queryKey: ['residentAnnouncements'] });
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to delete announcement');
    },
  });
};

export const useChangeAnnouncementStatus = () => {
  const queryClient = useQueryClient();

  return useMutation<
    ChangeAnnouncementStatusResponse,
    Error,
    { id: string; payload: ChangeAnnouncementStatusPayload }
  >({
    mutationFn: ({ id, payload }) =>
      announcementApi.changeAnnouncementStatus(id, payload),

    onSuccess: (data) => {
      toast.success(data.message || 'Status updated successfully');
      // Invalidate announcements list + details cache
      queryClient.invalidateQueries({ queryKey: ['residentAnnouncements'] });
      queryClient.invalidateQueries({
        queryKey: ['residentAnnouncementDetails'],
      });
    },

    onError: (error) => {
      toast.error(error.message || 'Failed to update status');
    },
  });
};

/**********************************
 * ADMIN ANNOUCEMENT MUTATIONS
 **********************************/
export const useCreateAnnouncement = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateAnnouncementPayload) =>
      announcementApi.create(payload),
    onSuccess: () => {
      // toast.success('Announcement created successfully');
      queryClient.invalidateQueries({ queryKey: ['announcements'] });
      queryClient.invalidateQueries({ queryKey: ['announcementOverview'] });
    },
    onError: (error: any) => {
      console.log(
        error?.response?.data?.message || 'Failed to create announcement',
      );
    },
  });
};

export const useDeleteAnnouncement = () => {
  // const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (announcementId: string) =>
      announcementApi.deleteAnnouncement(announcementId),

    onSuccess: (response) => {
      console.log('✅ Announcement deleted:', response);
    },

    onError: (error: any) => {
      console.error(
        '❌ Failed to delete announcement:',
        error.response?.data?.message || error.message,
      );
    },
  });
};
